/**
 * AI token tiers (trial / freemium / subscriber / unlimited pass) and soft-launch flags.
 * When REACT_APP_AI_LIMITS_ENFORCED is not "true", daily blocking still uses storage AI_DAILY_TOKEN_BUDGET (1M);
 * subscription toasts use nominal tier thresholds (7-day trial 20k/day, then 4k/day freemium; same caps on web and native) for App Store prep.
 */

import { Capacitor } from '@capacitor/core';
import { getAiTokenUsageDay, formatDate, AI_DAILY_TOKEN_BUDGET_DISABLED } from './storage';
import { isFirestoreProEntitlementActive } from './userFirestoreEntitlement';

/** Match `storage.js` AI_DAILY_TOKEN_BUDGET until limits are enforced (single source for blocking). */
export const AI_DISPLAY_CAP_WHEN_LIMITS_OFF = 1_000_000;

export const AI_TRIAL_CALENDAR_DAYS = 7;
/** Free tier: generous allowance for the first week, then ongoing cap. */
export const AI_TOKENS_TRIAL_PER_DAY = 20_000;
export const AI_TOKENS_FREEMIUM_PER_DAY = 4_000;
export const AI_TOKENS_SUBSCRIBED_PER_DAY = 250_000;

/** When false (default), hard caps stay at legacy 1M; nudges still run if enabled. */
export const AI_LIMITS_ENFORCED =
  String(process.env.REACT_APP_AI_LIMITS_ENFORCED || 'true').toLowerCase() === 'true';

/** Toast nudges for subscription / freemium (Apple prep). Set REACT_APP_AI_SUBSCRIPTION_TOASTS=false to silence. */
export const AI_SUBSCRIPTION_TOASTS_ENABLED =
  String(process.env.REACT_APP_AI_SUBSCRIPTION_TOASTS || 'true').toLowerCase() !== 'false';

const trialKey = (uid) => `rbm_ai_trial_started_at_${uid}`;
const subCacheKey = (uid) => `rbm_native_pro_sub_${uid}`;
const unlimitedKey = (uid) => `rbm_ai_unlimited_until_${uid}`;

const formatYmd = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const calendarDaysBetween = (startYmd, endYmd) => {
  if (!startYmd || !endYmd) return 0;
  const a = new Date(`${startYmd}T12:00:00`);
  const b = new Date(`${endYmd}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.round((b - a) / 86_400_000);
};

/**
 * First signed-in open seeds trial anchor (per Firebase uid).
 */
export function ensureAiTrialStartedForUid(uid) {
  if (!uid || typeof localStorage === 'undefined') return;
  const k = trialKey(uid);
  if (!localStorage.getItem(k)) {
    localStorage.setItem(k, new Date().toISOString());
  }
}

export function getAiTrialStartYmd(uid) {
  if (!uid || typeof localStorage === 'undefined') return '';
  const raw = localStorage.getItem(trialKey(uid));
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return formatYmd(d);
}

export function setCachedNativeSubActiveForUid(uid, active) {
  if (!uid || typeof localStorage === 'undefined') return;
  localStorage.setItem(subCacheKey(uid), active ? '1' : '0');
}

function getCachedNativeSubActiveForUid(uid) {
  if (!uid || typeof localStorage === 'undefined') return false;
  return localStorage.getItem(subCacheKey(uid)) === '1';
}

/** True when StoreKit / Play entitlement cache says Pro (native only; never from web). */
export function isNativeProSubCachedActive(uid) {
  if (!Capacitor.isNativePlatform()) return false;
  return getCachedNativeSubActiveForUid(uid);
}

export function setUnlimitedPassUntilIso(uid, iso) {
  if (!uid || typeof localStorage === 'undefined') return;
  if (!iso) {
    localStorage.removeItem(unlimitedKey(uid));
    return;
  }
  localStorage.setItem(unlimitedKey(uid), String(iso));
}

export function hasActiveUnlimitedPassForUid(uid) {
  if (!uid || typeof localStorage === 'undefined') return false;
  const iso = localStorage.getItem(unlimitedKey(uid));
  if (!iso) return false;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return false;
  return t > Date.now();
}

/**
 * @returns {'unlimited_pass'|'subscriber'|'trial'|'freemium'}
 */
export function getAiUsageTierForUid(uid) {
  if (!uid) return 'freemium';
  if (hasActiveUnlimitedPassForUid(uid)) return 'unlimited_pass';
  // StoreKit / Play cache is meaningless on web (and was incorrectly set from App bootstrap).
  if (Capacitor.isNativePlatform() && getCachedNativeSubActiveForUid(uid)) return 'subscriber';
  if (isFirestoreProEntitlementActive(uid)) return 'subscriber';
  const startYmd = getAiTrialStartYmd(uid);
  if (!startYmd) return 'trial';
  const today = formatYmd(new Date());
  if (calendarDaysBetween(startYmd, today) < AI_TRIAL_CALENDAR_DAYS) return 'trial';
  return 'freemium';
}

export function getNominalDailyTokenCapForTier(tier) {
  switch (tier) {
    case 'unlimited_pass':
      return Number.POSITIVE_INFINITY;
    case 'subscriber':
      return AI_TOKENS_SUBSCRIBED_PER_DAY;
    case 'trial':
      return AI_TOKENS_TRIAL_PER_DAY;
    default:
      return AI_TOKENS_FREEMIUM_PER_DAY;
  }
}

/** Denominator for Settings / Home meter (1M while limits off). */
export function getDisplayDailyTokenCapForUid(uid) {
  if (!AI_LIMITS_ENFORCED) return AI_DISPLAY_CAP_WHEN_LIMITS_OFF;
  const tier = getAiUsageTierForUid(uid);
  const cap = getNominalDailyTokenCapForTier(tier);
  return Number.isFinite(cap) ? cap : AI_DISPLAY_CAP_WHEN_LIMITS_OFF;
}

/**
 * Single snapshot for meter, toasts, and send blocking so cap/tier cannot disagree across the UI.
 * @param {string} uid
 * @param {{ date?: string, used?: number } | null} [usageOverride] - e.g. right after addAiTokenUsage returns
 */
export function getAiTokenDayBudgetState(uid, usageOverride = null) {
  const usage = usageOverride && typeof usageOverride === 'object'
    ? {
        date: String(usageOverride.date || ''),
        used: Math.max(0, Number(usageOverride.used) || 0),
      }
    : getAiTokenUsageDay();
  const today = formatDate(new Date());
  const tier = getAiUsageTierForUid(uid);
  const cap = getDisplayDailyTokenCapForUid(uid);
  const used = Math.max(0, Number(usage.used) || 0);
  const overBudget =
    !AI_DAILY_TOKEN_BUDGET_DISABLED
    && Number.isFinite(cap)
    && cap > 0
    && usage.date === today
    && used >= cap;
  return { usage, today, tier, cap, used, overBudget };
}

export function getAiTokenMeterUsedAndCap(uid) {
  const b = getAiTokenDayBudgetState(uid);
  return { used: b.used, cap: b.cap };
}

export function formatAiTokenMeterLine(uid) {
  const b = getAiTokenDayBudgetState(uid);
  return `AI Tokens Today: ${b.used.toLocaleString()} / ${b.cap.toLocaleString()}`;
}

/** Show the daily meter only for trial / post-trial free tier (hide when StoreKit Pro or Firestore manual Pro). */
export function shouldShowAiTokenMeterForUid(uid) {
  if (!uid) return false;
  const tier = getAiUsageTierForUid(uid);
  return tier === 'trial' || tier === 'freemium';
}

/**
 * Settings → Account: show daily token usage for all tiers that track usage, including Pro (250k/day nominal).
 * Home intentionally uses {@link shouldShowAiTokenMeterForUid} only.
 */
export function shouldShowSettingsAiTokenMeterForUid(uid) {
  if (!uid) return false;
  const tier = getAiUsageTierForUid(uid);
  return tier === 'trial' || tier === 'freemium' || tier === 'subscriber' || tier === 'unlimited_pass';
}

/** Native iOS/Android only: web skips tier nudges. */
export function shouldRunSubscriptionTierNudges() {
  return Capacitor.isNativePlatform();
}
